// MyProjectCreate.js
import React, { useCallback, useEffect, useState, useRef } from 'react';
import useFetch from '../../hooks/useFetch';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../layouts/MainLayout';
import { GenerateThink5whysProjectCode } from '../../components/GenerateThink5whysCode';

const MyProjectCreate = () => {
  const authState = useSelector(state => state.authReducer);
  const [fetchData] = useFetch();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [createSuccess, setCreateSuccess] = useState(false); 
  const [updateSuccess, setUpdateSuccess] = useState(false); 

  const [createSuccessMsg, setCreateSuccessMsg] = useState('');
  const [updateSuccessMsg, setUpdateSuccessMsg] = useState(''); 
 
  const [creatingProject, setCreatingProject] = useState(false); 
   
  const [verifyParentProjectSuccess, setVerifyParentProjectSuccess] = useState(false); 

  const [errors, setErrors] = useState({});
  
  const [parentProjectCreatorName, setParentProjectCreatorName] = useState(''); 
  const [autoProjectCode, setAutoTaskCode] = useState(GenerateThink5whysProjectCode());

  const userId = authState.user._id;
  const [formData, setFormData] = useState({
    createdBy: authState.user._id,
    creatorCode: authState.user.think5whyscode,
    creatorName: authState.user.name,
    projectType: 'FRI', // Initialize with appropriate default or leave empty
    projectCode: autoProjectCode, 
    projectTitle: '',
    projectDesc: '',
    projectDos: '',
    projectDont: '',
    projectMicroPage: '',
    myProjectRemarks: '',
    dateCreated: new Date(),
    isActive: 1,
    isDeleted: 0,
    masterProjectCode: '',
    masterProjectTitle: '',
    masterProjectCreator: '',
    isLevel: false,
    entityCode: '',
    myProjectMembers: null,
  });

  const [createClicked, setCreateClicked] = useState(false);

  const handleCreateClick = () => {
    if(formData.projectTitle==='')
      {
        errors.projectTitle="Title Must Not Be Blank";
        return;
      }
    setCreatingProject(true);
    fetchProjectCreate();
  };

  const fetchProjectCreate = useCallback(() => {
    // console.log('userId:', userId);
  
    try {
      const config = {
        url: "/myprojects",
        method: 'post',
        data: formData,
        headers: { Authorization: authState.token },
      };
  
      // console.log('Creating project with config:', config);
  
      fetchData(config)
        .then(response => {
          if (response && response.status) {
            // console.log("Project created successfully:", response);
            setCreateSuccess(true);
            setCreateSuccessMsg("Project Created Successfully");
            errors.projectTitle='';
          } else {
            console.error("Failed to create profile:", response);
            setCreateSuccessMsg("Sorry! Profile Not Created. Please retry.");
          }
        })
        .catch(error => {
          console.error("Error creating profile:", error);
          setCreateSuccessMsg("Sorry! Profile Not Created. Please retry.");
        });
    } catch (error) {
      console.error('Error in fetchProfileCreate:', error);
      setCreateSuccessMsg("Sorry! Profile Not Created. Please retry.");
    }
  }, [userId, formData, authState, fetchData, setCreateSuccess, setCreateSuccessMsg]);

  useEffect(() => {
    if (!authState.isLoggedIn) return;
}, [authState.isLoggedIn, fetchProjectCreate, setCreateSuccess, setCreateSuccessMsg]);


const handleVerifyParentProjectClick = () => {
    // console.log("handleVerifyParentProjectClick", formData);

    setVerifyParentProjectSuccess(true);
    
    // Validate project Code
    if (/^TT[a-zA-Z0-9]{12}$/.test(formData.masterProjectCode)) {
        if (formData.masterProjectCode === formData.projectCode) {
            setErrors(`Master Project Code is ${formData.masterProjectCode} and Your Project Code is ${formData.projectCode}. Both must be different!`);
            return;
          } 
          fetchParentProjectCode(formData.projectCode);
    } else {
        setErrors('Please enter a valid 14-character Think5Whys Project Code starting with TT.');
    }
};


const fetchParentProjectCode = useCallback(() => {
  // console.log("formData.masterProjectCode for API call ", formData.masterProjectCode);
  const config = {
      url: "/myprojects/verifyprojectcode",
      method: "post",
      headers: { Authorization: authState.token },
      data: { projectCode: formData.masterProjectCode }
  };

  fetchData(config)
      .then(response => {
        // console.log("API Response: ", response); 
          if (response && response.status) {
              // console.log("formData.masterProjectCode found ", response);
              setParentCodeStatus(true);
              setParentProjectTitle(response.projectcodeinfo.projectTitle);
              setParentProjectCreatorName(response.projectcodeinfo.creatorName);
              setParentProjectStatusMsg("Parent Project Code found. 😊");
              // console.log("Payee Code found");
          } else {
              // console.log("Payee Code Not found");
              setParentProjectStatusMsg(`Parent Project Code ${formData.masterProjectCode} not found! 😔 Make sure the Parent Project Code is valid.`);
          }
      })
      .catch(error => {
          // console.log("Parent Project Code Not found!", error);
          setParentProjectStatusMsg("Error in getting data");
      });
}, [fetchData, authState.token]);


useEffect(() => {
  if (!authState.isLoggedIn) return;
}, [authState.isLoggedIn, fetchParentProjectCode]);


  const handleBackToMyGroupsListClick = () => {
    navigate('/myprofile-projects');
  };

  const handleUpdateClick = async () => {
    try {
      const response = await fetch(`/myprojects/${formData._id}`, {
        method: 'put',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        // Update state or handle success as needed
        // console.log('Project updated successfully.');
      } else {
        console.error('Failed to update project:', response.statusText);
      }
    } catch (error) {
      console.error('Error updating project:', error.message);
    }
  };

  return (
    <MainLayout>
      <div className='text-lg mt-12 mx-8 border-b border-b-gray-300 text-white'>
        <h2>Profile - Project / Groups Creation</h2>
      </div>
      <div className="m-auto my-2 p-2 border-2 shadow-md rounded-md md:w-7/12 w-full text-white text-sm">
        <div className="flex flex-col justify-left mt-2 items-center">
          <div>
            <p className='text-sm text-yellow-200 mr-2'>Think5Whys Project Code: {autoProjectCode} </p>
          </div>
          <div>
            <p className='text-sm text-white mb-1 mt-2'>Input Project Title: </p>
          </div>          
          <div className='w-6/12 flex flex-row mt-2'>
            <input
              type="text"
              className="profile-input text-black"
              style={{
                border: '1px solid #ccc',
                padding: '8px',
                width: '100%',
                boxSizing: 'border-box',
              }} 
              value={formData.projectTitle}
              onChange={(e) => setFormData({ ...formData, projectTitle: e.target.value })}
              placeholder="Enter project title"
            />
          </div>
          <div>
            <button
              className='bg-purple-700 mt-2'
              onClick={handleCreateClick}
              disabled={createSuccess}
              style={{ maxWidth: '150px' }}
            >
              Create Project
            </button>
            <br/>
            {errors.projectTitle !== '' && (
              <p className='text-red-500 text-lg'>{errors.projectTitle}</p>
            )}
          </div>
        </div>
        {createSuccess && (
          <div className='text-sm'>
            <div className="mt-2 border-t">
              <p className='text-lg text-green-400'>Project {formData.projectTitle} Created Successfully!</p>
              <p className='text-lg text-orange-400'>Fill the other details!</p>
            </div>
            <div className="flex flex-col mt-1 text-sm">
              <label>Project Type:</label>
              <select className="profile-select text-black" value={formData.projectType} onChange={(e) => setFormData({ ...formData, projectType: e.target.value })}>
                <option value={"TEA"}>Team</option>
                <option value={"DEP"}>Departmental</option>
                <option value={"BRA"}>Branch</option>
                <option value={"ZON"}>Zonal</option>
                <option value={"REG"}>Regional</option>
                <option value={"COM"}>Company Wide</option>
                <option value={"INS"}>Institutional</option>
                <option value={"ADM"}>Administartive</option>
                <option value={"MIN"}>Ministry</option>
                <option value={"PRO"}>Professional</option>
                <option value={"ACA"}>Academic</option>
                <option value={"MAN"}>Management</option>
                <option value={"OTH"}>Others</option>

              </select>
              {errors.projectType && <span className="error">{errors.projectType}</span>}
          </div>
          <div className="flex flex-col">
            <label>Project Description</label>
            <textarea
              className="profile-textarea"
              style={{
                border: '1px solid #ccc',
                padding: '2px',
                backgroundColor: '#f0f0f0', 
                color: '#000000',
                maxWidth: '650px', 
                maxHeight: '150px' 
              }}
              value={formData.projectDesc}
              onChange={(e) => setFormData({ ...formData, projectDesc: e.target.value })}
              placeholder="Enter project description"
            />
          </div>
          <div className="flex flex-col">
            <label>Project Do's</label>
            <textarea
              className="profile-textarea"
              style={{
                border: '1px solid #ccc',
                padding: '2px',
                backgroundColor: '#f0f0f0', 
                color: '#000000',
                maxWidth: '650px', 
                maxHeight: '150px' 
              }}
              value={formData.projectDos}
              onChange={(e) => setFormData({ ...formData, projectDos: e.target.value })}
              placeholder="Enter project do's"
            />
          </div>
          <div className="flex flex-col">
            <label>Project Don'ts</label>
            <textarea
              className="profile-textarea"
              style={{
                border: '1px solid #ccc',
                padding: '2px',
                backgroundColor: '#f0f0f0', 
                color: '#000000',
                maxWidth: '650px', 
                maxHeight: '150px' 
              }}
              value={formData.projectDont}
              onChange={(e) => setFormData({ ...formData, projectDont: e.target.value })}
              placeholder="Enter project don'ts"
            />
          </div>
          <div className="flex flex-col">
            <label>Micro Page Link</label>
            <input
              type="text"
              className="profile-input"
              style={{
                border: '1px solid #ccc',
                padding: '2px',
                backgroundColor: '#f0f0f0', 
                color: '#000000',
                width: '90%'
              }}
              value={formData.projectMicroPage}
              onChange={(e) => setFormData({ ...formData, projectMicroPage: e.target.value })}
              placeholder="Enter micro page link"
            />
          </div>
          <div className="flex flex-col">
            <label>Project Remarks</label>
            <textarea
              className="profile-textarea"
              style={{
                border: '1px solid #ccc',
                padding: '2px',
                backgroundColor: '#f0f0f0', 
                color: '#000000',
                maxWidth: '650px', 
                maxHeight: '150px' 
              }}
              value={formData.myProjectRemarks}
              onChange={(e) => setFormData({ ...formData, myProjectRemarks: e.target.value })}
              placeholder="Enter project remarks"
            />
          </div>
          <div className="flex flex-col">
            <p>Parent Project</p>
            <input
              type="text"
              className="profile-input"
              style={{
                border: '1px solid #ccc',
                padding: '2px',
                backgroundColor: '#f0f0f0', 
                color: '#000000',
                width: '150px'
              }}
              value={formData.masterProjectCode}
              onChange={(e) => setFormData({ ...formData, masterProjectCode: e.target.value })}
              placeholder="Enter parent project code"
            />
            {/* <button
              className='bg-orange-700 mt-2'
              onClick={handleVerifyParentProjectClick}
              disabled={verifyParentProjectSuccess}
              style={{ maxWidth: '250px' }}
            >
              Verify Parent Project
            </button> */}
          </div>
          <div className="flex flex-col">
              <label>
              <input className='mr-4' type="checkbox" checked={formData.isLevel} onChange={(e) => setFormData({ ...formData, isLevel: e.target.checked })} />
              Project Has Hierarchy?
              </label>
          </div>
          <div className="flex justify-center mt-4">
            <button
              className='bg-purple-700'
              onClick={handleUpdateClick}
              style={{ maxWidth: '150px' }}
            >
              Update
            </button>
            <button
              className='bg-purple-700 ml-4'
              onClick={handleBackToMyGroupsListClick}
              style={{ maxWidth: '250px' }}
            >
              Back To My Project / Group List
            </button>
          </div>
        </div>
        )}
      </div>
    </MainLayout>
  );
};

export default MyProjectCreate;
